import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Heart, Flame, Sparkles, Users, Bell } from "lucide-react";
import { VerifiedBadge } from "@/components/Badges";
import { Link } from "react-router-dom";

/**
 * "Your CLING impact" strip. Shows people helped, weekly rhythm, strongest
 * topics, recent thank-you tags. Renders a friendly empty state until the
 * helper has their first resolution.
 */
export function ImpactStrip({ variant = "home" }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/me/impact").then(({ data }) => setData(data)).catch(() => {}); }, []);
  if (!data) return null;

  const hasHelped = (data.people_helped || 0) > 0 || data.topic_strengths?.length > 0;

  if (!hasHelped) {
    return (
      <div className="mt-8 border rounded-2xl bg-[color:hsl(var(--amber))]/5 border-[color:hsl(var(--amber))]/30 p-4 flex items-start gap-3" data-testid="impact-empty">
        <Sparkles className="w-4 h-4 text-[color:hsl(var(--amber))] shrink-0 mt-0.5" />
        <div className="text-sm">
          <span className="font-semibold">Ready to help someone?</span>{" "}
          <span className="text-muted-foreground">Turn on notifications for a topic you know. When someone needs help, you'll see it here — and your first thank-you shows up on your profile.</span>
          {data.pending_invitations > 0 && (
            <div className="mt-1 text-xs text-[color:hsl(var(--amber))] font-medium inline-flex items-center gap-1">
              <Bell className="w-3 h-3" /> {data.pending_invitations} pending invitation{data.pending_invitations > 1 ? "s" : ""}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-8 rise-in" data-testid="impact-strip">
      <div className="flex items-center gap-2 text-xs font-mono-label text-[color:hsl(var(--amber))]">
        <Heart className="w-3 h-3" /> Your CLING impact
      </div>
      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="People helped" value={data.people_helped} icon={Users} accent />
        <StatCard label="Solved this week" value={data.this_week_helped} icon={Flame} />
        <StatCard label="Pending invites" value={data.pending_invitations} icon={Bell} link="/rooms" />
        <div className="border rounded-2xl bg-card p-4" data-testid="impact-verification">
          <div className="text-xs font-mono-label text-muted-foreground">Trust</div>
          <div className="mt-1 flex items-center flex-wrap gap-1">
            <VerifiedBadge level={data.verification_level} />
            {data.verification_level === "none" && <span className="text-xs text-muted-foreground">Community Verified after 5 resolutions</span>}
          </div>
        </div>
      </div>

      {(data.topic_strengths?.length > 0 || data.recent_thank_you_tags?.length > 0) && (
        <div className="mt-3 grid md:grid-cols-2 gap-3">
          {data.topic_strengths?.length > 0 && (
            <div className="border rounded-2xl bg-card p-4" data-testid="impact-topics">
              <div className="text-xs font-mono-label text-[color:hsl(var(--teal))]">Strongest topics</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {data.topic_strengths.map((t) => (
                  <span key={t.category} className="inline-flex items-center gap-1 rounded-full border bg-[color:hsl(var(--teal))]/10 text-[color:hsl(var(--teal))] px-3 py-1 text-xs" data-testid={`topic-${t.category}`}>
                    {t.category} <span className="opacity-70">· {t.helped}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
          {data.recent_thank_you_tags?.length > 0 && (
            <div className="border rounded-2xl bg-card p-4" data-testid="impact-tags">
              <div className="text-xs font-mono-label text-[color:hsl(var(--amber))]">Recent thank-yous</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {data.recent_thank_you_tags.map((t) => (
                  <span key={t.tag} className="inline-flex items-center gap-1 rounded-full border bg-[color:hsl(var(--amber))]/10 text-[color:hsl(var(--amber))] px-3 py-1 text-xs" data-testid={`tag-${t.tag.replace(/\s/g,'-')}`}>
                    {t.tag} <span className="opacity-70">× {t.count}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {variant === "profile" && (
        <div className="mt-3 text-xs text-muted-foreground">
          <Link to="/rooms" className="hover:text-foreground">See rooms you've helped in →</Link>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, accent, link }) {
  const Body = (
    <>
      <div className="text-xs font-mono-label text-muted-foreground flex items-center gap-1">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className={`mt-1 font-display font-extrabold text-2xl sm:text-3xl ${accent ? "text-[color:hsl(var(--teal))]" : "text-foreground"}`}>
        {value ?? 0}
      </div>
    </>
  );
  const cls = "border rounded-2xl bg-card p-4 transition hover:border-[color:hsl(var(--teal))]";
  if (link) return <Link to={link} className={cls}>{Body}</Link>;
  return <div className={cls}>{Body}</div>;
}
